import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { DrawerLayout } from 'src/components/Drawer';
import { Fab } from 'src/components/Fab';
import { Icon } from 'src/components/Icon';
import { InfiniteLoadTrigger } from 'src/components/InfiniteLoadTrigger';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { Scroller } from 'src/components/Scroller';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { ListCardSkeleton } from 'src/shared/ListCard';
import { NoResults } from 'src/shared/NoResults';
import { formatFullDate } from 'src/utils/dateFormatter';

import { AppointmentCard } from './AppointmentCard';
import { AppointmentDrawer } from './AppointmentDrawer/AppointmentDrawer';
import { AppointmentsHeader } from './AppointmentsHeader';
import { useAppointmentsPage } from './useAppointmentsPage';

export default function AppointmentsPage() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'appointments.page',
  });
  const {
    appointments,
    appointmentsLoading,
    appointmentsFetching,
    appointmentsError,
    curAppointment,
    setCurAppointmentId,
    fetchNextPage,
    filter,
    filterLabels,
    dateLabels,
    hasNextAppointments,
    filterDate,
    handleClearDate,
    allStaff,
    handleScroll,
    handleScrollTop,
    isScrolled,
    scrollerRef,
    hasDependents,
    dependents,
  } = useAppointmentsPage();
  const { session } = useSession();

  return (
    <DrawerLayout drawerOpen={!!curAppointment}>
      <Meta title={t('mySessions')} brand={session?.brand} />

      <Stack maxHeight="100dvh">
        <AppointmentsHeader hasDependents={hasDependents} />

        <Scroller direction="column" onScroll={handleScroll} ref={scrollerRef}>
          <Container maxWidth="md">
            <Box sx={{ pb: 8, pt: 2 }}>
              {isScrolled && !filterDate && (
                <Fab
                  color="primary"
                  onClick={handleScrollTop}
                  size="small"
                  sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                  }}
                >
                  <Icon icon={icon({ name: 'arrow-up', style: 'regular' })} />
                </Fab>
              )}
              {filterDate && (
                <>
                  <Typography variant="subtitle2" sx={{ pb: 3, pt: 1 }}>
                    {t('sessions')} {dateLabels[filter]}{' '}
                    {formatFullDate(filterDate)}
                  </Typography>
                  <Fab
                    color="primary"
                    variant="extended"
                    onClick={handleClearDate}
                    size="medium"
                    sx={{
                      position: 'fixed',
                      bottom: 16,
                      right: 16,
                    }}
                  >
                    <Stack spacing={1} direction="row" alignItems="center">
                      <Icon
                        icon={icon({ name: 'arrow-up', style: 'regular' })}
                      />
                      <Typography
                        variant="button"
                        sx={{ textTransform: 'none' }}
                      >
                        {t('today')}
                      </Typography>
                    </Stack>
                  </Fab>
                </>
              )}
              {!appointmentsError &&
                !appointmentsLoading &&
                !appointments?.length && (
                  <NoResults
                    title={t('noAppointments', {
                      filter: filterLabels[filter],
                    })}
                    icon={icon({ name: 'calendar-plus', style: 'regular' })}
                  >
                    <Button
                      variant="outlined"
                      component={Link}
                      href={externalLinks.v3Url('/booking/')}
                    >
                      {t('bookAppointment')}
                    </Button>
                  </NoResults>
                )}
              {appointmentsLoading && (
                <Stack spacing={4}>
                  <ListCardSkeleton />
                  <ListCardSkeleton />
                  <ListCardSkeleton />
                </Stack>
              )}

              <Stack spacing={4}>
                {appointments?.map((appointment) => (
                  <AppointmentCard
                    onClick={() => setCurAppointmentId(appointment.id)}
                    showStaff={allStaff == 'true'}
                    appointment={appointment}
                    dependents={dependents}
                    key={appointment.id}
                  />
                ))}
              </Stack>
              <InfiniteLoadTrigger
                fetching={appointmentsFetching}
                onFetch={() => hasNextAppointments && fetchNextPage()}
              >
                {appointmentsFetching && !appointmentsLoading && (
                  <CircularProgress size="small" />
                )}
              </InfiniteLoadTrigger>
            </Box>
          </Container>
        </Scroller>
      </Stack>

      <AppointmentDrawer
        appointment={curAppointment}
        dependents={dependents}
        onClose={() => setCurAppointmentId(null)}
      />
    </DrawerLayout>
  );
}
